import React, { Component } from "react";
import ReactDOM from "react-dom";
import { core } from "react-page-maker";
import { connect } from "react-redux";

import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import state from "../state";
import store from "../core/redux/store";
import { l, resetId } from "../functions";
import GKEvent from "../events";

// functional component which extends Dropzone but with some fixed properties - id and parentID
// both props set to `root` - required since application state holds `root` as parent node
// default initDone set to true
const Canvas = (props) => (
  <Dropzone {...props} parentID="root" id="root" initDone />
);

class Dropzone extends Component {
  constructor(props) {
    super(props);

    // create refrence for canvas
    // to check whether is dragging on current canvas or not
    this.canvasRef = React.createRef();

    // component state
    // initialElements - to keep track of initial elements
    // droppedElements - to keep track of newly added elements
    this.state = {
      initialElements: [],
      droppedElements: [],
      initDone: false,
    };
  }

  componentWillMount() {
    this._setInitialElements(this.props.initialElements);
  }

  componentWillUnmount() {
    // fix Warning: Can't perform a React state update on an unmounted component
    this.setState = (state, callback) => {
      return;
    };
  }

  componentWillReceiveProps({ initialElements, version, id }) {
    if (initialElements !== null && this.props.version !== version) {
      this.setState(
        {
          initDone: false,
        },
        () => {
          this._setInitialElements(initialElements);
        }
      );
    }
  }

  /**
   * function to allow manual element update
   * Note - function is only accessible through ref and make sure
   * valid data is pass through else application state/hierarchy can break
   * @param data {Array/Function} - if its function then make sure you are
   * returning valida array
   * @param done {function} - success call back function
   */
  dangerouslySetElements = (data, done) => {
    let elements = [];
    if (typeof data === "function")
      elements = data(this.state.droppedElements) || [];
    else elements = data;

    const { id: dropzoneID, parentID } = this.props;

    elements = elements.map((e) => ({
      ...e,
      dropzoneID,
      parentID,
      showBasicContent: false,
      updateState: this._updateState,
      removeElement: this._removeElement,
      duplicateElement: this._duplicateElement,
      updateElement: this._updateElement,
      flushDroppedElements: this._flushDroppedElements,
      checkAndRemoveElement: this._checkAndRemoveElement,
    }));

    this.setState(
      {
        initialElements: elements,
        droppedElements: elements,
      },
      () => this._updateState(done)
    );
  };

  /**
   * function to remove before/after class form all canvas elements
   */
  _unmarkDragElements = () => {
    [].forEach.call(
      this.canvasRef.current.querySelectorAll(".drag-item"),
      (e) => e.classList.remove("before", "after")
    );
  };

  /**
   * function to set initial elements
   * @param initialElements {Array} - It holds all initial elements to be shown in canvas
   */
  _setInitialElements = (initialElements) => {
    // current component state(droppedElements) doesn't hold any elements
    // but initialElements has some element to be set
    const gotInitialItems =
      Array.isArray(initialElements) &&
      // initialElements.length &&
      !this.state.initDone;

    // for first time add initialElements to droppedElements

    if (gotInitialItems) {
      const { id: dropzoneID, parentID } = this.props;
      const updatedInitialItems = initialElements.map((e) => ({
        ...e,
        key: e.id,
        dropzoneID,
        parentID,
        showBasicContent: false,
        updateState: this._updateState,
        removeElement: this._removeElement,
        duplicateElement: this._duplicateElement,
        updateElement: this._updateElement,
        flushDroppedElements: this._flushDroppedElements,
        checkAndRemoveElement: this._checkAndRemoveElement,
      }));

      this.setState(
        {
          initialElements: updatedInitialItems,
          droppedElements: updatedInitialItems,
        },
        () =>
          this._updateState(() => {
            this.setState({
              initDone: true,
            });
          })
      );
    }
  };

  /**
   * function to toggle/manage class(inside)
   * class gets appended to canvas
   * @param target {DOM} - current canvas upon which use is hovering
   * @param action {String} - add/remove - name of action
   */
  _manageInsideClass = ({ target }, action) => {
    if (this.canvasRef.current === target) {
      target.classList[action]("inside");
    }
  };

  /**
   * function to remove element from droppedElements
   * @param elementID - {String} - ID of element
   * @param cb - {function}
   * @param dispatchElementRemove {Boolean} - trigger `removeElement` event if its true
   */
  _removeElement = (elementID, cb = () => {}, dispatchElementRemove) => {
    const index = this.state.droppedElements.findIndex(
      (e) => e.id === elementID
    );

    this.setState(
      {
        droppedElements: this.state.droppedElements.filter(
          (d, i) => i !== index
        ),
      },
      () => this._updateState(cb, dispatchElementRemove ? elementID : null)
    );
  };

  _duplicateElement = (elementID, parentID, external) => {
    const index = this.state.droppedElements.findIndex(
      (e) => e.id === elementID
    );
    let droppedElements = [].concat(this.state.droppedElements),
      element = resetId(droppedElements[index], parentID);

    if (external) {
      store.dispatch({ type: "COPY", toCopy: element });
    } else {
      if (index > 0) {
        droppedElements = [
          ...droppedElements.slice(0, index),
          element,
          ...droppedElements.slice(index),
        ];
      } else {
        droppedElements = [element, ...droppedElements];
      }

      this.setState(
        {
          droppedElements: droppedElements,
        },
        () => {
          this._updateState();
        }
      );
    }
  };

  /**
   * function to update element from droppedElements
   * @param newData - {element} - { id, name, type, payload }
   * @param cb - {function}
   */
  _updateElement = (newData, cb = () => {}) => {
    const elementIndex = this.state.droppedElements.findIndex(
      (e) => e.id === newData.id
    );

    if (elementIndex === -1) return cb(null);

    // support is limited to below keys only, to avoid possible breaks
    const supportedKeys = ["name", "type", "payload"];
    const newElementData = {};

    Object.keys(newData).forEach((key) => {
      if (supportedKeys.indexOf(key) !== -1) newElementData[key] = newData[key];
    });

    this.setState(
      {
        droppedElements: this.state.droppedElements.map((e) => {
          if (e.id === newData.id) return { ...e, ...newElementData };
          return e;
        }),
      },
      () => this._updateState(cb)
    );

    return true;
  };

  /**
   * function to update the application state (not component state)
   * function will further call `updateState` from state API, which updates the application state
   * @param cb {function} - callback function - optional
   */
  _updateState = (cb = () => {}, dispatchElementRemove) => {
    const { id: dropzoneID, parentID } = this.props;

    state.updateState(
      dropzoneID,
      parentID,
      this.state.droppedElements,
      cb,
      dispatchElementRemove
    );
  };

  /**
   * function to flush the component state
   * once component state ths flushed, this will call `_updateState` to update the
   * application state
   * @param cb {function} - callback function - optional
   */
  _flushDroppedElements = (cb = () => {}) => {
    this.setState(
      {
        initialElements: [],
        droppedElements: [],
      },
      () => this._updateState(cb)
    );
  };

  /**
   * function check below parameters
   * 1 - user's intend is to move element from one canvas to other
   * 2 - Get confirmation before remove
   * once both condition matched remove the element from current canvas
   */
  _checkAndRemoveElement = () => {
    const elementMoved = core.getDraggedElement();
    const { removeElement, elementID } = elementMoved;
    const status = this.props.onElementMove(elementMoved);
    const attemptToMove = !core.getAttemptToRemove();

    if (status && attemptToMove) removeElement(elementID);
  };

  // user is dragging over the canvas
  // `preventDefault` - Required. Allows us to drop.
  // @param e {event}
  _onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  /**
   * user is leaving the canvas
   * @param e {event}
   */
  _onDragLeave = (e) => {
    this._manageInsideClass(e, "remove");
    core.setAttemptToRemove(true);
  };

  /**
   * user is dragging over canvas
   * @param e {event}
   */
  _onDragEnter = (e) => {
    store.dispatch({ type: "SET_SHOW_SIDEBAR", showSideBar: false });
    this._manageInsideClass(e, "add");
    core.setAttemptToRemove(false);
    const { droppedElements } = this.state;

    if (droppedElements.length === 0) {
      if (document.getElementById("previewdragover-before"))
        document.getElementById("previewdragover-before").remove();
      if (document.getElementById("previewdragover-after"))
        document.getElementById("previewdragover-after").remove();

      if (!document.getElementById("previewdragover-inner")) {
        let dropMessage = l("Drop Here");
        let className = "";

        if (
          store.getState().rootReducer.draggedElement &&
          store.getState().rootReducer.draggedElement.type === "block" &&
          this.props.id !== "root"
        ) {
          className = "danger";
          dropMessage = "Drop outside";
        }
        var newItems =
          '<div id="previewdragover-inner" class="' +
          className +
          '"><span>' +
          dropMessage +
          "</span></div>";
        e.target.insertAdjacentHTML("afterbegin", newItems);
      }
    }
  };

  /**
   * use has dropped the element into the canvas
   * perform all required checks
   * function also triggers `onDrop` prop so that uset can provide additional info
   * once all done `_addElement` get called with all required info
   * @param e {event}
   */
  _onDrop = (e) => {
    store.dispatch({ type: "SET_SHOW_SIDEBAR", showSideBar: true });
    e.preventDefault();
    e.stopPropagation();
    const { droppedElements } = this.state;

    core.setAttemptToRemove(false);
    this._manageInsideClass(e, "remove");

    const { onDrop } = this.props;
    let data = JSON.parse(e.dataTransfer.getData("data"));
    data = { ...data };

    this._unmarkDragElements();

    if (document.getElementById("previewdragover-before"))
      document.getElementById("previewdragover-before").remove();
    if (document.getElementById("previewdragover-after"))
      document.getElementById("previewdragover-after").remove();
    if (document.getElementById("previewdragover-inner"))
      document.getElementById("previewdragover-inner").remove();

    return onDrop
      ? onDrop(data, this._addElement, {
          dropIndex: !droppedElements.length ? core.getDropPostion() + 1 : 0,
          currentElements: droppedElements,
        })
      : this._addElement(data);
  };

  /**
   * function to add element into droppedElements
   * prior to add it perfom below check
   * 1 - element has unique key
   * 2 - element has invalid data
   * 3 - check against max-capacity
   * once element added remove the element from previous canvas
   * @param updatedData {Object} - element information
   * @returns Object - new list of canvas elements
   */

  _addElement = (updatedDatas) => {
    const { id: dropzoneID, capacity, parentID, allowHorizontal } = this.props;
    const dropPosition = core.getDropPostion();
    const draggedElement = core.getDraggedElement();

    if (dropzoneID === "root") {
      if (updatedDatas?.payload?.fullContainer)
        updatedDatas.payload.fullContainer = false;
      else if (updatedDatas?.length > 0)
        updatedDatas[0].payload.fullContainer = false;
    }

    let elementsToAdd;

    if (!Array.isArray(updatedDatas)) elementsToAdd = [updatedDatas];
    else elementsToAdd = updatedDatas;

    let newElements = [].concat(this.state.droppedElements);
    let elementAlreadyRemoved = false;
    let isSameIndex = false;

    if (elementsToAdd[0].type === "block" && dropzoneID !== "root")
      return {
        dropzoneID,
        elements: newElements,
      };

    elementsToAdd.forEach((updatedData) => {
      const invalidUpdatedData = !updatedData || !updatedData.id;

      let indexOfPresentElement = -1;

      const keyAlreadyPresent =
        updatedData.id &&
        this.state.droppedElements.filter((e, i) => {
          if (e.id === updatedData.id) {
            indexOfPresentElement = i;
            return true;
          }

          return false;
        }).length;

      isSameIndex = indexOfPresentElement === dropPosition;

      // check fo unique key
      if (invalidUpdatedData || keyAlreadyPresent) {
        // if (isSameIndex) return core.error("Duplicate or invalid ID");

        elementAlreadyRemoved = true;

        newElements = newElements.map((e) => {
          if (e.id === updatedData.id) return { ...e, remove: true };

          return e;
        });
      }

      const elementToDrop = {
        ...updatedData,
        key: updatedData.id,
        dropzoneID,
        parentID,
        allowHorizontal,
        showBasicContent: false,
        updateState: this._updateState,
        removeElement: this._removeElement,
        duplicateElement: this._duplicateElement,
        updateElement: this._updateElement,
        // initialElements helps figuring out initDone
        // initialElements: [],
        flushDroppedElements: this._flushDroppedElements,
        checkAndRemoveElement: this._checkAndRemoveElement,
      };

      if (dropPosition > 0) {
        newElements = [
          ...newElements.slice(0, dropPosition),
          elementToDrop,
          ...newElements.slice(dropPosition),
        ];
      } else {
        newElements = [elementToDrop, ...newElements];
      }
    });

    if (!isSameIndex) newElements = newElements.filter((e) => !e.remove);

    // check new list against max-capacity
    if (capacity && newElements.length > capacity)
      return core.error(
        `Maximum capacity of canvas(${dropzoneID}) is ${capacity}`
      );

    //Check for duplicated and if no then return true to prevent losing the element
    const uniqueIds = [];
    newElements = newElements.filter((element) => {
      const isDuplicate = uniqueIds.includes(element.id);

      if (!isDuplicate) {
        uniqueIds.push(element.id);
        return true;
      }
    });

    // update the current list
    this.setState(
      {
        droppedElements: newElements,
      },
      () => {
        // remove element from previous canvas
        if (
          !elementAlreadyRemoved &&
          draggedElement &&
          typeof draggedElement.checkAndRemoveElement === "function"
        )
          draggedElement.checkAndRemoveElement();

        // update the application state
        this._updateState();
      }
    );

    // adding dropping class to dropzone, it can be used for animation purpose
    // this.canvasRef.current.classList.add("dropping");
    // setTimeout(() => {
    //   if (this.canvasRef.current)
    //     this.canvasRef.current.classList.remove("dropping");
    // }, 500);
    // this.setState(
    //   {
    //     droppedElements: this.state.droppedElements.map((e) => {
    //       if (e.id === newData.id) return { ...e, ...newElementData };
    //       return e;
    //     }),
    //   },
    //   () => this._updateState(cb)
    // );

    return true;
  };

  /**
   * function to render the dragged element
   * @param props {Object} - An object which hold the info of dragged element/component
   * @returns JSX
   */
  _renderDragItem = (props) => {
    const element = core
      .getRegisteredPaletteElements()
      .find((e) => e.type === props.type);
    return element ? <element.component {...props} /> : null;
  };

  _getLayouts = () => {
    let layouts = [];

    for (const page of store.getState().rootReducer.fetchedPages) {
      if (page.type === "layout_payload") layouts.push(page);
    }

    return layouts;
  };

  render() {
    const { droppedElements } = this.state;
    const { capacity, id, placeholder, parentID } = this.props;
    const spaceAvailable = capacity ? capacity > droppedElements.length : true;

    return (
      <div
        ref={this.canvasRef}
        className={`${!spaceAvailable ? "no-space" : ""} ${
          id === "root" ? "canvas" : ""
        } dropzone`}
        onDragOver={this._onDragOver}
        onDragLeave={this._onDragLeave}
        onDragEnter={this._onDragEnter}
        onDrop={this._onDrop}
        onDragEnd={this._onDragEnd}
      >
        {!droppedElements.length ? (
          <>
            <div className="empty"></div>
          </>
        ) : (
          droppedElements.map((e, i) =>
            this._renderDragItem({
              ...e,
              index: i,
              spaceAvailable,
              initDone: this.state.initDone,
              dropzoneProps: {
                initDone: this.state.initDone,
                parentID: e.id,
              },
            })
          )
        )}

        {!droppedElements.length && placeholder ? (
          <div
            className={`dropzone-placeholder ${
              parentID === "root" && this._getLayouts().length > 0
                ? ""
                : "no-layout"
            }`}
          >
            <div
              className="gk-custom-message inline"
              style={{ margin: "auto", maxWidth: 300 }}
            >
              <p>{placeholder}</p>
            </div>
            {/* 							parentID === 'root' && this._getLayouts().length > 0 && <div className="import-request">
								<span><span>{l('OR')}</span></span>
								<button
									onClick={() => {
										GKEvent.importLayout();
									}}
									className="gk-button gk-small gk-primary gk-default"
								>
									<span>{l('Import layout')}</span>
								</button>
							</div> */}
          </div>
        ) : null}
      </div>
    );
  }
}

Dropzone.propTypes = {
  id: PropTypes.string.isRequired,
  capacity: PropTypes.number,
  onDrop: PropTypes.func,
  onElementMove: PropTypes.func,
  allowHorizontal: PropTypes.bool,
  initialElements: PropTypes.arrayOf(Object),
  parentID: PropTypes.string.isRequired,
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};

Dropzone.defaultProps = {
  initialElements: [],
  placeholder: "Drop Here",
  onElementMove: () => true,
};

const mapStateToProps = (state) => {
  // window.$undo = state.undo;
  return {
    elementToEdit: state.rootReducer.elementToEdit || [],
  };
};

class PropMyDraggable extends Component {
  constructor(props) {
    super(props);

    this.dragElemRef = React.createRef();
    this.state = {
      draggable: false,

      translateX: 0,
      translateY: 0,
      posTop: null,
      posLeft: null,
      posRight: null,
    };
  }

  componentDidMount() {
    const node = ReactDOM.findDOMNode(this);
    let dragger = node.querySelector(".dragger");
    let draggerBtn = node.querySelector(".dragger-btn");

    if (dragger) {
      dragger.addEventListener("mousedown", () => {
        const posTop =
          node.getBoundingClientRect().top +
          node.ownerDocument.defaultView.pageYOffset;
        const posLeft =
          node.getBoundingClientRect().left +
          node.ownerDocument.defaultView.pageXOffset;

        this.setState({
          draggable: true,
          posTop: posTop,
          posLeft: posLeft,
        });
      });
      dragger.addEventListener("mouseup", () => {
        this.setState({
          draggable: false,
          posTop: null,
          posLeft: null,
        });
      });
    }

    if (draggerBtn) {
      draggerBtn.addEventListener("mousedown", () => {
        const posTop =
          node.getBoundingClientRect().top +
          node.ownerDocument.defaultView.pageYOffset;
        const posRight =
          node.getBoundingClientRect().left +
          node.getBoundingClientRect().width +
          node.ownerDocument.defaultView.pageXOffset;

        this.setState({
          draggable: true,
          posTop: posTop,
          posRight: posRight,
        });
      });
      draggerBtn.addEventListener("mouseup", () => {
        this.setState({
          draggable: false,
          posTop: null,
          posLeft: null,
        });
      });
    }

    this.dragImg = new Image(0, 0);
    this.dragImg.src =
      "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
  }

  componentWillUnmount() {
    // fix Warning: Can't perform a React state update on an unmounted component
    this.setState = (state, callback) => {
      return;
    };
  }

  componentWillReceiveProps(nextProps) {
    // update the state once parent state initialisation is done
    if (this.props.initDone !== nextProps.initDone && nextProps.initDone) {
      this.props.updateState();
    }
  }

  _dragEnd = (e) => {
    e.stopPropagation();
    this.dragElemRef.current.classList.remove("before", "after");

    if (document.getElementById("previewdragover-before"))
      document.getElementById("previewdragover-before").remove();
    if (document.getElementById("previewdragover-after"))
      document.getElementById("previewdragover-after").remove();
    if (document.getElementById("previewdragover-inner"))
      document.getElementById("previewdragover-inner").remove();

    // done dragging, reset dragged element
    core.setDraggedElement(null);
    store.dispatch({ type: "DRAG_ELEMENT", element: null });

    this.setState({
      translateX: 0,
      translateY: 0,
      draggable: false,
    });
  };

  _dragStart = (e) => {
    e.stopPropagation();

    const {
      id,
      type,
      name,
      fields,
      payload,
      dropzoneID,
      removeElement,
      options,
      elementId,
      checkAndRemoveElement,
    } = this.props;

    const data = {
      id,
      type,
      name,
      options,
      elementId: elementId || id,
      payload,
    };

    if (fields) {
      data.fields = fields;
    }

    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData("data", JSON.stringify(data)); // required, we cann't pass JS object
    e.dataTransfer.setDragImage(this.dragImg, 0, 0);

    store.dispatch({ type: "DRAG_ELEMENT", element: data });

    // if element is already present in some canvas
    // then set draggedElement, so that this will help to remove the element from previous canvas
    if (dropzoneID) {
      core.setDraggedElement({
        elementID: id,
        dropzoneID,
        removeElement,
        checkAndRemoveElement,
      });
    }
  };

  /**
   * function to set drop position.
   * first fine mid of element upon which user is dragging over and
   * based on that decide whether user trying to drop an element above or below
   */
  _onDragOver = (e) => {
    const elemCord = this.dragElemRef.current.getBoundingClientRect();

    if (!this.props.spaceAvailable) {
      return false;
    }

    if (this.props.allowHorizontal) {
      const dragElemX = e.clientX;
      if (dragElemX >= elemCord.x && dragElemX <= elemCord.x + elemCord.width) {
        const midX = elemCord.x + elemCord.width / 2;
        if (dragElemX < midX) {
          this.dragElemRef.current.classList.remove("after");
          this.dragElemRef.current.classList.add("before");
          core.setDropPostion(this.props.index);
        } else {
          this.dragElemRef.current.classList.remove("before");
          this.dragElemRef.current.classList.add("after");
          core.setDropPostion(this.props.index + 1);
        }
      }
    } else {
      const dragElemY = e.clientY;
      const elemHeight = elemCord.height;

      if (
        dragElemY >= elemCord.y &&
        dragElemY <= parseInt(elemCord.y + elemHeight)
      ) {
        let beforeY, afterY;

        beforeY = elemCord.y + elemHeight * 0.5;
        afterY = elemCord.y + elemHeight * 0.5;

        let oppId, elemId;
        var domTarget;

        if (dragElemY < beforeY) {
          this.dragElemRef.current.classList.remove("after");
          this.dragElemRef.current.classList.add("before");
          core.setDropPostion(this.props.index);

          elemId = "before";
          oppId = "after";

          domTarget = "beforebegin";
        } else if (dragElemY >= afterY) {
          this.dragElemRef.current.classList.remove("before");
          this.dragElemRef.current.classList.add("after");
          core.setDropPostion(this.props.index + 1);

          elemId = "after";
          oppId = "before";

          domTarget = "afterend";
        }

        if (document.getElementById("previewdragover-inner"))
          document.getElementById("previewdragover-inner").remove();

        if (document.getElementById("previewdragover-" + oppId))
          document.getElementById("previewdragover-" + oppId).remove();

        if (document.getElementById("previewdragover-" + elemId)) {
          const element = document.getElementById("previewdragover-" + elemId);
          const children = Array.from(
            this.dragElemRef.current.parentNode.children
          );
          let toRemove = true;

          children.forEach((child) => {
            if (child.id == "previewdragover-" + elemId) {
              toRemove = false;
              return;
            }
          });

          if (toRemove) element.remove();
        } else {
          let dropMessage = l("Drop Here");
          let className = "";

          if (
            store.getState().rootReducer.draggedElement &&
            store.getState().rootReducer.draggedElement.type === "block" &&
            this.props.id !== "root" &&
            this.props.parentID !== "root"
          ) {
            className = "danger";
            dropMessage = "Drop outside";
          }
          var newItems =
            '<div id="previewdragover-' +
            elemId +
            '" class="' +
            className +
            '"><span>' +
            dropMessage +
            "</span></div>";

          this.dragElemRef.current.insertAdjacentHTML(domTarget, newItems);
        }
      }
    }

    return true;
  };

  _onDragLeave = () => {
    // remove before/after class from dragged element
    this.dragElemRef.current.classList.remove("before", "after");
  };

  _draggin = ({ clientX, clientY }) => {
    const node = ReactDOM.findDOMNode(this);

    const posTop = this.state.posTop;
    const posLeft = this.state.posLeft;
    const posRight = this.state.posRight;

    if (posTop !== null && posLeft !== null)
      this.setState({
        translateX: clientX - posLeft + 2,
        translateY: clientY - posTop + 2,
      });
    else if (posTop !== null && posRight !== null)
      this.setState({
        translateX: clientX - posRight + 2,
        translateY: clientY - posTop + 2,
      });
    else
      this.setState({
        translateX: 0,
        translateY: 0,
      });
  };

  render() {
    const {
      id,
      name,
      elementProps,
      allowHorizontal,
      minify,
      side,
      parentID,
      type,
    } = this.props;
    const { translateX, translateY, draggable } = this.state;
    let e = null;

    if (this.props.dropzoneID) {
      // add this required function only if element is dropped in canvas
      e = {
        onDragOver: this._onDragOver,
        onDragLeave: this._onDragLeave,
      };
    }

    if (side || draggable) {
      e = {
        ...e,
        draggable: true,
      };
    }

    let stylePosition;
    if (draggable)
      stylePosition = {
        transform: `translate(${translateX}px, ${translateY}px)`,
        zIndex: 100,
      };
    else stylePosition = {};

    const activeParentId = this.props.elementToEdit
      ? this.props.elementToEdit.parentID
      : null;
    const activeId = this.props.elementToEdit
      ? this.props.elementToEdit.id
      : null;

    return (
      <div
        id={id}
        style={{ ...stylePosition, ...this.props?.style }}
        ref={this.dragElemRef}
        className={`drag-item tohover ${
          activeId === id ? "active-element" : ""
        } ${activeParentId === id ? "active-parent" : ""} ${
          id.includes("gk-") ? "can" : ""
        }`}
        onDragStart={this._dragStart}
        onDragEnd={this._dragEnd}
        onDrag={this._draggin}
        onClick={(e) => {
          e.stopPropagation();
          store.dispatch({
            type: "SHOW_BLOCK_SETTINGS",
            showBlockSettings: false,
          });
          if (this.props.payload)
            store.dispatch({ type: "EDIT_ELEMENT", element: this.props });
        }}
        {...elementProps}
        {...e}
      >
        <button className="dragger-btn">
          <FontAwesomeIcon icon={["fal", "arrows"]} />
        </button>
        {this.props.children}
      </div>
    );
  }
}

const MyDraggable = connect(mapStateToProps)(PropMyDraggable);

MyDraggable.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  initDone: PropTypes.bool,
  index: PropTypes.number,
  allowHorizontal: PropTypes.bool,
  fields: PropTypes.instanceOf(Array),
  draggable: PropTypes.bool,
  spaceAvailable: PropTypes.bool,
  updateState: PropTypes.func,
  dropzoneID: PropTypes.string,
  payload: PropTypes.instanceOf(Object),
  elementProps: PropTypes.instanceOf(Object),
  type: PropTypes.string.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.element,
  ]).isRequired,
  removeElement: PropTypes.func,
  checkAndRemoveElement: PropTypes.func,
};

MyDraggable.defaultProps = {
  checkAndRemoveElement: () => true,
  elementProps: null,
  payload: null,
  draggable: true,
  updateState: () => true,
};

export { Canvas, Dropzone, MyDraggable };
